import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { REACT_APP_API_URL } from "../../../../../env";
import axios from "axios";

const columns = [
  {
    name: "Property",
    selector: (row) => row.PropertyName,
    sortable: true,
    minWidth: "180px",
  },
  {
    name: "Booking Ref No",
    selector: (row) => row.BookingRefNo,
    sortable: true,
  },
  {
    name: "Customer Name",
    selector: (row) => row.CustomerName,
    sortable: true,
  },
  { name: "Booking Date", selector: (row) => row.IssueDate, sortable: true },
  { name: "Cancel Date", selector: (row) => row.BillDate, sortable: true },
  { name: "Check-in", selector: (row) => row.CheckinDate, sortable: true },
  { name: "Check-out", selector: (row) => row.CheckOutDate, sortable: true },
  {
    name: "Refund Amount",
    selector: (row) => row.Amount,
    sortable: true,
    right: true,
  },

  {
    name: "Tax",
    selector: (row) => row.TaxAmount,
    sortable: true,
    right: true,
  },
  {
    name: "Cancel Amount",
    selector: (row) => row.Discount,
    sortable: true,
    right: true,
  },
  {
    name: "Total",
    selector: (row) => row.TotalAmount,
    sortable: true,
    right: true,
  },
  // { name: "Bill Date", selector: (row) => row.BillDate },
];


const customStyles = {
  rows: {
    style: {
      minHeight: "45px",
    },
  },
  headCells: {
    style: {
      fontWeight: "bold",
      fontSize: "14px",
      textAlign: "center",
      width: "170px",
    },
  },
  cells: {
    style: {
      border: "1px solid #dee2e6",
    },
  },
};

const PartialCancelList = () => {
  const [data, setData] = useState([]);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(null);
  const [dateError, setDateError] = useState("");

  // Filters state
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [searchBookingRef, setSearchBookingRef] = useState("");

  useEffect(() => {
    fetch(`${REACT_APP_API_URL}api/dashboard/admin/history/partial-cancelled`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch data");
        return res.json();
      })
      .then((result) => {
        if (result.success) {
          setData(result.data);
        } else {
          setError("No data received");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setPending(false));
  }, []);

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (
        searchBookingRef &&
        !row.BookingRefNo.toLowerCase().includes(searchBookingRef.toLowerCase())
      )
        return false;

      if (filterFrom) {
        const fromDate = new Date(filterFrom);
        const checkinDate = new Date(row.CheckinDate);
        if (checkinDate < fromDate) return false;
      }
      if (filterTo) {
        const toDate = new Date(filterTo);
        const checkinDate = new Date(row.CheckinDate);
        if (checkinDate > toDate) return false;
      }
      return true;
    });
  }, [data, filterFrom, filterTo, searchBookingRef]);

  // Excel export function
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PartialCancelled");
    XLSX.writeFile(wb, "PartialCancelledBookings.xlsx");
  };

  // PDF export function
  const exportPDF = () => {
    const doc = new jsPDF();
    const exportColumns = columns.filter((col) => col.name !== "Action");
    const tableColumn = exportColumns.map((col) => col.name);
    const tableRows = filteredData.map((row) =>
      exportColumns.map((col) => {
        const val = col.selector(row);
        return val !== undefined ? val.toString() : "";
      })
    );

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    doc.save("PartialCancelledBookings.pdf");
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setFilterFrom(value);
    if (filterTo && value && new Date(value) > new Date(filterTo)) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    setFilterTo(value);
    if (filterFrom && value && new Date(filterFrom) > new Date(value)) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3 mb-3">
        <div className="col-12">
          {/* Page Title outside card */}
          <h2 className="mb-3">Partial Cancelled Booking List</h2>

          {/* Card that contains filters + table */}
          <div className="card shadow-sm rounded-1 p-3">
            {/* Filters in one row */}
            <div className="d-flex flex-wrap gap-3 align-items-end p-3 border rounded mb-3">
              {/* From Date */}
              <div className="d-flex flex-column">
                <label className="form-label mb-1">From Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filterFrom}
                  onChange={handleFromDateChange}
                  max={filterTo || undefined}
                />
              </div>

              {/* To Date */}
              <div className="d-flex flex-column">
                <label className="form-label mb-1">To Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filterTo}
                  onChange={handleToDateChange}
                  min={filterFrom || undefined}
                />
              </div>

              {/* Search Booking Ref */}
              <div className="d-flex flex-column">
                <label className="form-label mb-1">Booking Ref</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search Booking Ref"
                  value={searchBookingRef}
                  onChange={(e) => setSearchBookingRef(e.target.value)}
                />
              </div>

              {/* Export Buttons */}
              <div className="d-flex flex-column">
                <label className="form-label mb-1">Export</label>
                <div className="d-flex gap-2">
                  <CSVLink
                    data={filteredData}
                    filename="PartialCancelledBookings.csv"
                    className="btn btn-sm btn-outline-primary"
                    title="Export CSV"
                  >
                    <i className="bi bi-file-earmark"></i>
                  </CSVLink>

                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={exportExcel}
                    title="Export Excel"
                  >
                    <i className="bi bi-file-earmark-excel"></i>
                  </button>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={exportPDF}
                    title="Export PDF"
                  >
                    <i className="bi bi-filetype-pdf"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Error Messages */}
            {dateError && (
              <div className="alert alert-danger py-2">{dateError}</div>
            )}
            {error && <div className="alert alert-danger">{error}</div>}

            {/* Table */}
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              paginationPerPage={100}
              paginationRowsPerPageOptions={[50, 100, 500]}
              responsive
              striped
              highlightOnHover
              progressPending={pending}
              customStyles={customStyles}
              noDataComponent="No cancelled bookings found."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartialCancelList;